/**
 * @file Assigns and unassigns attachment labels on one file. Consumer: the
 * attachments panel file row, beside the importance control.
 *
 * The offer is built by walking `groupOrder` and asking `labelIsOffered`, so the
 * administrator's declared order is the offered order, no consumer sorts, and
 * the settings table's offered column can never drift from what this shows. When
 * nothing is left to offer the trigger stays mounted and disabled, and the
 * tooltip and its accessible name say why.
 *
 * `onChange` receives the whole next id array and is called on the click, with
 * no debounce of its own: the write path a file's other edits use already owns
 * one, and a second window only widens the gap in which a concurrent immediate
 * write cancels a pending one. That write is debounced, so `labelIds` still
 * holds the old assignment when the next tick of a burst arrives; `pending`
 * carries the ticks made since, keyed by the `labelIds` they were derived from
 * so a changed prop supersedes it with no effect and nothing to synchronise.
 * It is dropped when the popover closes, so an assignment whose write was
 * rejected cannot leave a tick standing for a change the artifact never took.
 *
 * That key is compared by value, never by identity: `mergeDocumentIntoProxy`
 * reassigns an array on reference and never diffs, so every snapshot of the
 * artifact hands this component a new one. The trade is that an equal-valued
 * assignment no longer supersedes `pending`, so a tick whose debounced write was
 * cancelled stays displayed until the popover closes.
 */

import { useState, type FC } from 'react';
import { Button, Checkbox, Popover, Tooltip, Typography } from 'antd';
import { TagsOutlined } from '@ant-design/icons';
import { isEqual } from 'lodash';
import { type Snapshot } from 'valtio';
import { type LabelId, type LabelSet } from '#types';
import FlexCol from '../../atoms/FlexCol';
import { LabelTags } from '../../atoms/LabelTags';
import { labelIsOffered } from '../../atoms/labelIsOffered';

const { Text } = Typography;

type PendingAssignment = {
    /** The `labelIds` these ticks were derived from; a different value supersedes them. */
    base: readonly LabelId[] | undefined;
    labelIds: LabelId[];
};

const offeredGroups = (set: Snapshot<LabelSet>, assigned: ReadonlySet<LabelId>) =>
    set.groupOrder.flatMap(groupId => {
        const group = set.groups[groupId];
        if (!group) return [];
        const labels = group.labels.filter(label => labelIsOffered(group, label, assigned));
        return labels.length === 0 ? [] : [{ id: group.id, name: group.name, labels }];
    });

export const AttachmentLabelPicker: FC<{
    set: Snapshot<LabelSet>;
    fileName: string;
    labelIds: readonly LabelId[] | undefined;
    disabled: boolean;
    onChange: (labelIds: LabelId[]) => void;
}> = ({ set, fileName, labelIds, disabled, onChange }) => {
    const [open, setOpen] = useState(false);
    const [pending, setPending] = useState<PendingAssignment | null>(null);

    const current = pending && isEqual(pending.base, labelIds) ? pending.labelIds : labelIds ?? [];
    const assigned = new Set(current);
    const groups = offeredGroups(set, assigned);
    const noneDefined = groups.length === 0;
    const blocked = disabled || noneDefined;

    const reason = noneDefined
        ? 'No attachment labels are defined.'
        : disabled
            ? 'This document is read-only, so its labels cannot be changed.'
            : null;

    const toggle = (labelId: LabelId, checked: boolean) => {
        const next = checked ? [...current, labelId] : current.filter(id => id !== labelId);
        setPending({ base: labelIds, labelIds: next });
        onChange(next);
    };

    const handleOpenChange = (next: boolean) => {
        setOpen(next);
        if (!next) setPending(null);
    };

    return (
        <Popover
            open={blocked ? false : open}
            onOpenChange={handleOpenChange}
            trigger="click"
            placement="bottomRight"
            destroyOnHidden
            title={<Text strong>{`Labels for ${fileName}`}</Text>}
            content={
                <FlexCol gap={10}>
                    {groups.map(group => (
                        <FlexCol key={group.id} gap={5}>
                            <Text type="secondary">{group.name}</Text>
                            {group.labels.map(label => (
                                <Checkbox
                                    key={label.id}
                                    checked={assigned.has(label.id)}
                                    onChange={event => toggle(label.id, event.target.checked)}
                                    aria-label={`${label.name} on ${fileName}`}
                                >
                                    <LabelTags set={set} labelIds={[label.id]} />
                                </Checkbox>
                            ))}
                        </FlexCol>
                    ))}
                </FlexCol>
            }
        >
            <Tooltip title={reason ?? `Assign labels to ${fileName}`}>
                <span style={{ display: 'inline-flex' }}>
                    <Button
                        size="small"
                        type="text"
                        icon={<TagsOutlined />}
                        disabled={blocked}
                        aria-label={reason ? `Labels for ${fileName}. ${reason}` : `Labels for ${fileName}`}
                    />
                </span>
            </Tooltip>
        </Popover>
    );
};
